<template>
  <div class="accreditation-order-page subscription-pass-page">
    <div v-if="loadError" class="order-error">
      <button type="button" class="order-back-button" @click="$router.back()">
        <v-icon size="20" color="#0277bd">arrow_back</v-icon>
        <span>{{ adminText('workflowsAll.attendee_list_87ae776', 'Attendee list') }}</span>
      </button>
      <accreditation-details-card>{{ workflowLabel(loadError) }}</accreditation-details-card>
    </div>
    <div v-else-if="$apollo.loading || !details" class="subscription-pass-skeleton" aria-busy="true" :aria-label="adminText('workflowsAll.loading_subscription_pass_details_6ec77ca', 'Loading subscription pass details')">
      <vue-skeleton-loader rounded radius="4" width="150px" height="24px" animation="fade" />
      <accreditation-details-layout>
        <template slot="main">
          <accreditation-details-card>
            <template slot="header">
              <div class="skeleton-card-heading">
                <vue-skeleton-loader rounded radius="4" width="190px" height="28px" animation="fade" />
                <vue-skeleton-loader rounded radius="4" width="230px" height="18px" animation="fade" />
              </div>
            </template>
            <div class="skeleton-detail-list">
              <vue-skeleton-loader rounded radius="4" width="100%" height="24px" animation="fade" />
              <vue-skeleton-loader rounded radius="4" width="100%" height="24px" animation="fade" />
            </div>
          </accreditation-details-card>

          <accreditation-details-card>
            <template slot="header">
              <div class="skeleton-card-heading">
                <vue-skeleton-loader rounded radius="4" width="70%" height="28px" animation="fade" />
                <vue-skeleton-loader rounded radius="4" width="180px" height="18px" animation="fade" />
              </div>
            </template>
            <div class="skeleton-detail-list">
              <vue-skeleton-loader
                v-for="row in 4"
                :key="`performance-row-${row}`"
                rounded
                radius="4"
                width="100%"
                height="24px"
                animation="fade"
              />
            </div>
          </accreditation-details-card>

          <accreditation-details-card>
            <template slot="header">
              <div class="skeleton-card-heading">
                <vue-skeleton-loader rounded radius="4" width="150px" height="28px" animation="fade" />
              </div>
            </template>
            <div class="skeleton-detail-list">
              <vue-skeleton-loader
                v-for="row in 3"
                :key="`user-row-${row}`"
                rounded
                radius="4"
                width="100%"
                height="24px"
                animation="fade"
              />
            </div>
          </accreditation-details-card>
        </template>

        <template slot="sidebar">
          <accreditation-details-card>
            <template slot="header">
              <div class="skeleton-card-heading">
                <vue-skeleton-loader rounded radius="4" width="65%" height="28px" animation="fade" />
                <vue-skeleton-loader rounded radius="4" width="80%" height="18px" animation="fade" />
                <vue-skeleton-loader rounded radius="4" width="80%" height="18px" animation="fade" />
              </div>
            </template>
            <div class="skeleton-detail-list">
              <vue-skeleton-loader rounded radius="4" width="100%" height="24px" animation="fade" />
              <vue-skeleton-loader rounded radius="4" width="100%" height="24px" animation="fade" />
              <vue-skeleton-loader rounded radius="4" width="100%" height="48px" animation="fade" />
            </div>
            <template slot="footer">
              <div class="skeleton-actions">
                <vue-skeleton-loader
                  v-for="button in 4"
                  :key="`action-${button}`"
                  rounded
                  radius="24"
                  width="100%"
                  height="48px"
                  animation="fade"
                />
              </div>
            </template>
          </accreditation-details-card>

          <accreditation-details-card>
            <template slot="header">
              <div class="history-header">
                <vue-skeleton-loader rounded radius="4" width="130px" height="28px" animation="fade" />
                <vue-skeleton-loader rounded radius="12" width="20px" height="20px" animation="fade" />
              </div>
            </template>
          </accreditation-details-card>
        </template>
      </accreditation-details-layout>
    </div>
    <template v-else>
      <button type="button" class="order-back-button" @click="$router.back()">
        <v-icon size="20" color="#0277bd">arrow_back</v-icon>
        <span>{{ adminText('workflowsAll.attendee_list_87ae776', 'Attendee list') }}</span>
      </button>

      <accreditation-details-layout>
        <template slot="main">
          <accreditation-details-card class="buyer-card">
            <template slot="header">
              <h1>{{ adminText('workflowsAll.order_100_ed40022', 'Order #100') }}{{ workflowLabel(details.order.id) }}</h1>
              <p>{{ adminText('workflowsAll.bought_online_b15ee82', 'Bought online') }} {{ workflowLabel(formatDateTime(details.order.createdAt)) }}</p>
            </template>
            <accreditation-details-row :label="adminText('workflowsAll.subscriber_16b20e6', 'Subscriber')" :value="subscriberName" />
            <accreditation-details-row :label="adminText('workflowsAll.email_84add5b', 'Email')" :value="subscriberEmail || '-'">
              <template slot="action">
                <button
                  type="button"
                  class="text-action"
                  :disabled="!subscriberEmail || actionLoading"
                  @click="sendPass"
                >
                  <v-icon size="20">mail_outline</v-icon>
                  <span>{{ adminText('workflowsAll.resend_0f4f469', 'Resend') }}</span>
                </button>
              </template>
            </accreditation-details-row>
          </accreditation-details-card>

          <accreditation-details-card v-if="userDetails.length || hasPassCode" class="user-details-card">
            <template slot="header">
              <h2>{{ adminText('workflowsAll.user_details_5082f10', 'User details') }}</h2>
            </template>
            <accreditation-details-row v-for="field in userDetails" :key="field.label" :label="workflowLabel(field.label)" :value="field.value" />
            <accreditation-details-row v-if="hasPassCode" :label="adminText('workflowsAll.qr_barcode_eb83f6c', 'QR / barcode')">
              <span class="code">{{ workflowLabel(passCode) }}</span>
            </accreditation-details-row>
          </accreditation-details-card>

          <accreditation-details-card class="performance-card">
            <template slot="header">
              <h2>{{ workflowLabel(details.event.title) }}</h2>
              <p>{{ workflowLabel(formatDateTime(details.event.from)) }}</p>
            </template>
            <accreditation-details-row :label="adminText('workflowsAll.venue_67cd406', 'Venue')" :value="venue" />
            <accreditation-details-row v-if="hasAssignedSeat" :label="adminText('workflowsAll.seat_b90b325', 'Seat')" :value="formattedSeat" />
            <accreditation-details-row :label="adminText('workflowsAll.access_status_4238215', 'Access status')" :value="details.attendee.status" />
          </accreditation-details-card>
        </template>

        <template slot="sidebar">
          <accreditation-details-card class="summary-card">
            <template slot="header">
              <h2>{{ workflowLabel(details.subscription.name) }}</h2>
              <p>{{ adminText('workflowsAll.valid_from_5c6bcf9', 'Valid From:') }} {{ workflowLabel(formatDate(details.subscription.valid_from)) }}</p>
              <p>{{ adminText('workflowsAll.valid_to_d3fe90f', 'Valid To:') }} {{ workflowLabel(formatDate(details.subscription.valid_to)) }}</p>
            </template>
            <div class="summary-body">
              <div class="summary-row">
                <span>{{ adminText('workflowsAll.payment_status_9dfea40', 'Payment status') }}</span>
                <strong>{{ workflowLabel(details.order.status) }}</strong>
              </div>
              <div class="summary-row">
                <span>{{ adminText('workflowsAll.subscription_pass_ec57207', 'Subscription pass') }}</span>
                <strong>{{ workflowLabel(formatMoney(details.order.price || details.subscription.price)) }}</strong>
              </div>
              <hr>
              <div class="summary-total">
                <span>{{ adminText('workflowsAll.access_2f81a22', 'Access') }}</span>
                <strong>{{ workflowLabel(details.attendee.status) }}</strong>
                <small>{{ adminText('workflowsAll.this_access_applies_only_to_the_selected_perf_1586f06', 'This access applies only to the selected performance.') }}</small>
              </div>
            </div>
            <template slot="footer">
              <subscription-pass-actions
                :can-print="Boolean(details.event.builder)"
                :loading="actionLoading"
                :revoke-disabled-reason="revokeDisabledReason"
                :revoke-label="revokeActionLabel"
                @download="downloadPass"
                @print="printAccreditation"
                @revoke="openRevokeDialog"
              />
            </template>
          </accreditation-details-card>

          <accreditation-details-card class="print-history-card">
            <template slot="header">
              <div class="history-header">
                <h2>{{ adminText('workflowsAll.print_history_c67fc1a', 'Print history') }}</h2>
                <button
                  type="button"
                  class="history-toggle-button"
                  :aria-expanded="String(printHistoryOpen)"
                  aria-controls="subscription-print-history"
                  @click="printHistoryOpen = !printHistoryOpen"
                >
                  <span class="visually-hidden">{{ adminText('workflowsAll.toggle_print_history_cea4bd9', 'Toggle print history') }}</span>
                  <v-icon size="20">{{ printHistoryOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}</v-icon>
                </button>
              </div>
            </template>
            <div v-show="printHistoryOpen" id="subscription-print-history" class="history-list">
              <div v-if="!details.attendee.prints.length" class="empty-history">{{ adminText('workflowsAll.no_prints_yet_ed9b462', 'No prints yet.') }}</div>
              <div v-for="print in details.attendee.prints" :key="print.id" class="history-row">
                <span>
                  <small>{{ workflowLabel(formatDateTime(print.createdAt)) }}</small>
                  <strong>{{ workflowLabel(print.user.firstName) }} {{ workflowLabel(print.user.lastName) }}</strong>
                </span>
                <span class="history-target">
                  <strong>{{ workflowLabel(subscriberName) }}</strong>
                  <v-icon size="20">assignment_ind</v-icon>
                </span>
              </div>
            </div>
          </accreditation-details-card>
        </template>
      </accreditation-details-layout>
    </template>

    <v-dialog v-model="showRevokeDialog" persistent max-width="520">
      <section class="revoke-access-dialog" role="dialog" aria-modal="true" aria-labelledby="revoke-access-dialog-title">
        <header class="revoke-access-dialog-header">
          <h2 id="revoke-access-dialog-title">
            {{ adminText('workflowsAll.remove_access_to_7121597', 'Remove access to') }} <strong>{{ workflowLabel(details && details.event.title) }} </strong>
            <span v-if="details && details.event.from">{{ workflowLabel(formatDateTime(details.event.from)) }} h</span>?
          </h2>
          <button
            type="button"
            class="revoke-access-dialog-close"
            :disabled="actionLoading"
            :aria-label="adminText('workflowsAll.close_dialog_7b29020', 'Close dialog')"
            @click="showRevokeDialog = false"
          >
            <v-icon size="22">close</v-icon>
          </button>
        </header>
        <div class="revoke-access-dialog-copy">
          <p v-if="hasAssignedSeat">{{ adminText('workflowsAll.this_seat_will_be_released_3695ea3', 'This seat will be released') }} <strong>{{ workflowLabel(formattedSeat) }}</strong>.</p>
          <p>{{ adminText('workflowsAll.your_subscription_and_access_to_all_other_per_a11e2e4', 'Your subscription and access to all other performances will remain unchanged.') }}</p>
          <p v-if="actionError" class="action-error" role="alert">{{ workflowLabel(actionError) }}</p>
        </div>
        <footer class="revoke-access-dialog-actions">
          <button type="button" class="revoke-access-dialog-button revoke-access-dialog-button-secondary" :disabled="actionLoading" @click="showRevokeDialog = false">
            {{ adminText('workflowsAll.cancel_77dfd21', 'Cancel') }}
          </button>
          <button type="button" class="revoke-access-dialog-button revoke-access-dialog-button-primary" :disabled="actionLoading" @click="revokeAccess">
            {{ adminText('workflowsAll.revoke_access_094386f', 'Revoke access') }}
          </button>
        </footer>
      </section>
    </v-dialog>
    <simple-snackbar :open.sync="snackbar.open" :message-prop="workflowLabel(snackbar.message)" :color="snackbar.color" />
  </div>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import axios from 'axios'
import moment from 'moment'
import {
  GET_THEATER_SUBSCRIPTION_PASS_DETAILS,
  REVOKE_THEATER_SUBSCRIPTION_PERFORMANCE_ACCESS,
} from '@/graphql/accreditation.graphql'
import { resendTheaterSubscriptionConfirmation } from '@/graphql/theater-subscriptions.graphql'
import SimpleSnackbar from './../shared/snackbars/simple-snackbar.vue'
import { frontendURL, parseTicketLocationDisplayFields } from './../../utils'
import AccreditationDetailsCard from './details/AccreditationDetailsCard.vue'
import AccreditationDetailsLayout from './details/AccreditationDetailsLayout.vue'
import AccreditationDetailsRow from './details/AccreditationDetailsRow.vue'
import SubscriptionPassActions from './theater-subscription/SubscriptionPassActions.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'theater-subscription-pass-details-container',
  components: {
    AccreditationDetailsCard,
    AccreditationDetailsLayout,
    AccreditationDetailsRow,
    SimpleSnackbar,
    SubscriptionPassActions,
  },
  props: {
    organizationSlug: String,
    eventId: String,
    accreditationUuid: String,
  },
  data() {
    return {
      details: null,
      loadError: null,
      actionLoading: false,
      showRevokeDialog: false,
      printHistoryOpen: true,
      actionError: null,
      snackbar: { open: false, message: '', color: 'primary' },
    }
  },
  apollo: {
    getTheaterSubscriptionPassDetails: {
      query: GET_THEATER_SUBSCRIPTION_PASS_DETAILS,
      variables() {
        return { eventId: this.eventId, accreditationUuid: this.accreditationUuid }
      },
      manual: true,
      result({ data, loading }) {
        if (!loading && data) this.details = data.getTheaterSubscriptionPassDetails
      },
      error(error) {
        this.loadError = error.message || 'workflowsAll.unable_to_load_subscription_pass_details_a331446'
      },
    },
  },
  computed: {
    subscriberName() {
      return this.details.subscriptionPurchase.customerName || this.buyerName || '-'
    },
    subscriberEmail() {
      return this.details.subscriptionPurchase.customerEmail || this.buyerEmail
    },
    buyerName() {
      const buyer = (this.details.order && this.details.order.buyer && this.details.order.buyer.fields) || {}

      return (
        [buyer.firstName || buyer['First name'], buyer.lastName || buyer['Last name']].filter(Boolean).join(' ') ||
        [this.details.order.user && this.details.order.user.firstName, this.details.order.user && this.details.order.user.lastName]
          .filter(Boolean)
          .join(' ') ||
        '-'
      )
    },
    buyerEmail() {
      const buyer = (this.details.order && this.details.order.buyer && this.details.order.buyer.fields) || {}

      return buyer.email || buyer.Email || (this.details.order.user && this.details.order.user.email) || ''
    },
    venue() {
      const location = this.details.event.location || {}

      return [location.venue, location.city].filter(Boolean).join(', ') || '-'
    },
    seat() {
      return this.details.attendee.ticketLocation || this.details.subscriptionPurchase.ticketLocation || 'Unnumbered'
    },
    formattedSeat() {
      const fields = parseTicketLocationDisplayFields(this.seat, {
        eventId: this.details.event.id,
        language: this.isAdminLocalization ? this.$adminLocale() : 'en',
      })

      return Object.entries(fields || {})
        .map(([label, value]) => `${label}: ${value}`)
        .join(', ')
    },
    passCode() {
      return this.details.subscriptionPurchase.qrCode || this.details.attendee.qrcode || this.details.attendee.barcode || '-'
    },
    hasPassCode() {
      return this.passCode !== '-'
    },
    hasAssignedSeat() {
      return Boolean(this.seat && this.seat !== 'Online Purchase' && this.seat !== 'Unnumbered')
    },
    hasPerformanceEnded() {
      return Boolean(this.details.event.to && moment(this.details.event.to).isSameOrBefore(moment()))
    },
    revokeDisabledReason() {
      if (this.details.attendee.status === 'DENIED') {
        return this.adminText('workflowsAll.access_for_this_performance_has_already_been__42ac991', 'Access for this performance has already been removed.')
      }
      if (this.details.attendee.status !== 'ALLOWED') {
        return this.adminText('workflowsAll.access_can_only_be_removed_while_the_accredit_d47d436', 'Access can only be removed while the accreditation is active.')
      }
      if (this.hasPerformanceEnded) {
        return this.adminText('workflowsAll.access_cannot_be_removed_after_the_performanc_8c5edf8', 'Access cannot be removed after the performance has ended.')
      }

      return ''
    },
    revokeActionLabel() {
      if (this.details.attendee.status === 'DENIED') return this.adminText('workflowsAll.access_revoked_cf70976', 'Access revoked')

      return this.adminText('workflowsAll.revoke_access_4230a95', 'Revoke access')
    },
    userDetails() {
      const fields = this.details.attendee.fields || {}

      return Object.entries(fields)
        .filter(([label, value]) => {
          return this.normalizedFieldKey(label) !== 'subscriptionid' && value !== null && value !== undefined && value !== ''
        })
        .map(([label, value], index) => ({
          label: this.formatFieldLabel(label),
          value: this.formatFieldValue(value),
          order: this.userDetailOrder(label, index),
        }))
        .sort((first, second) => first.order - second.order)
        .map(({ label, value }) => ({ label, value }))
    },
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format('DD.MM.YYYY') : '-'
    },
    formatDateTime(value) {
      return value ? moment(value).format('DD.MM.YYYY HH:mm') : '-'
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'

      const price = String(value).trim()
      const [, rawAmount, currency = '€'] = price.match(/^([+-]?[\d.,]+)\s*(.*)$/) || []
      const amount = Number(rawAmount && rawAmount.replace(',', '.'))

      if (Number.isNaN(amount)) return price

      return `${amount.toFixed(2)} ${currency || '€'}`
    },
    formatFieldValue(value) {
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'object') return Object.values(value).filter(Boolean).join(', ') || '-'

      return String(value)
    },
    formatFieldLabel(label) {
      const labels = {
        firstname: 'First Name',
        lastname: 'Last Name',
        email: 'Email',
        validfrom: 'Valid From',
        validto: 'Valid To',
        subscription: 'Subscription',
        subscriptionname: 'Subscription',
      }

      return labels[this.normalizedFieldKey(label)] || label.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ')
    },
    normalizedFieldKey(label) {
      return String(label).replace(/[^a-zA-Z0-9]/g, '').toLowerCase()
    },
    userDetailOrder(label, index) {
      const positions = {
        firstname: 0,
        lastname: 1,
        email: 2,
        validfrom: 3,
        validto: 4,
        subscription: 5,
        subscriptionname: 5,
      }
      const position = positions[this.normalizedFieldKey(label)]

      return position === undefined ? 100 + index : position
    },
    showSnackbar(message, color = 'primary') {
      this.snackbar = { open: true, message, color }
    },
    getErrorMessage(error, fallback) {
      const graphQLError = error && error.graphQLErrors && error.graphQLErrors[0]
      const networkError = error && error.networkError
      const responseError = networkError && networkError.result && networkError.result.errors && networkError.result.errors[0]

      return (graphQLError && graphQLError.message) || (responseError && responseError.message) || fallback
    },
    downloadBase64Pdf(base64, filename) {
      const bytes = window.atob(base64)
      const data = new Uint8Array(bytes.length)
      for (let index = 0; index < bytes.length; index += 1) data[index] = bytes.charCodeAt(index)
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
    async downloadPass() {
      this.actionLoading = true
      try {
        const lang = this.details.order.language || 'hr'
        const { data } = await axios.get(
          `${process.env.VUE_APP_SERVER_URL}/files/theater-subscription-by-uuid/${this.details.order.uuid}?lang=${lang}`,
        )
        this.downloadBase64Pdf(data.body, `CoreEvent-Subscription-${this.details.subscriptionPurchase.id}.pdf`)
      } catch (error) {
        this.showSnackbar(this.getErrorMessage(error, 'workflowsAll.unable_to_download_subscription_pass_8298151'), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    async sendPass() {
      this.actionLoading = true
      try {
        await this.$apollo.mutate({
          mutation: resendTheaterSubscriptionConfirmation,
          variables: {
            purchaseId: Number(this.details.subscriptionPurchase.id),
            organizationSlug: this.organizationSlug,
            lang: this.details.order.language || 'hr',
          },
        })
        this.showSnackbar('workflowsAll.subscription_pass_sent_successfully_9aa993d')
      } catch (error) {
        this.showSnackbar(this.getErrorMessage(error, 'workflowsAll.unable_to_send_subscription_pass_fe7efbf'), 'error')
      } finally {
        this.actionLoading = false
      }
    },
    printAccreditation() {
      const url = `${frontendURL}/organizations/${this.organizationSlug}/events/${this.details.event.id}/accreditation/${this.accreditationUuid}/edit/print`
      window.open(url, '_blank', 'noopener')
    },
    openRevokeDialog() {
      this.actionError = null
      this.showRevokeDialog = true
    },
    async revokeAccess() {
      this.actionError = null
      this.actionLoading = true
      try {
        await this.$apollo.mutate({
          mutation: REVOKE_THEATER_SUBSCRIPTION_PERFORMANCE_ACCESS,
          variables: { eventId: this.eventId, accreditationUuid: this.accreditationUuid },
        })
        await this.$apollo.queries.getTheaterSubscriptionPassDetails.refetch()
        this.showRevokeDialog = false
        this.showSnackbar('workflowsAll.access_for_the_selected_performance_has_been__3cafe56')
      } catch (error) {
        this.actionError = this.getErrorMessage(error, 'workflowsAll.unable_to_remove_access_4e5c97c')
        this.showSnackbar(this.actionError, 'error')
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>

<style scoped>
.accreditation-order-page {
  color: #333333;
}

.subscription-pass-skeleton {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.skeleton-card-heading,
.skeleton-detail-list,
.skeleton-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.skeleton-detail-list {
  gap: 24px;
}

.order-back-button {
  align-items: center;
  background: transparent;
  border: 0;
  color: #0277bd;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 700;
  gap: 8px;
  margin-bottom: 24px;
  padding: 0;
}

.summary-row span,
.history-row small {
  color: #665f5c;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
}

.summary-row strong,
.history-row strong {
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
  font-weight: 600;
  overflow-wrap: anywhere;
}

.code {
  font-family: monospace !important;
}

.text-action {
  align-items: center;
  background: transparent;
  border: 0;
  color: #0277bd;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Raleway', sans-serif;
  font-size: 18px;
  font-weight: 700;
  gap: 8px;
  padding: 8px 0;
  text-transform: uppercase;
}

.text-action:disabled {
  cursor: default;
  opacity: 0.5;
}

.summary-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.summary-row {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
}

.summary-body hr {
  border: 0;
  border-top: 1px solid #d9d9d9;
  margin: 0;
  width: 100%;
}

.summary-total {
  align-items: baseline;
  display: grid;
  grid-template-columns: 1fr auto;
}

.summary-total span,
.summary-total strong {
  color: #333333;
  font-family: 'Source Sans Pro', sans-serif;
  font-weight: 700;
}

.summary-total span {
  font-size: 16px;
}

.summary-total strong {
  font-size: 24px;
}

.summary-total small {
  color: #979797;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 12px;
  grid-column: 1 / 3;
}

.history-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.print-history-card::v-deep .accreditation-details-card-heading {
  background: #ffffff;
}

.history-header h2 {
  color: #666666;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.72px;
  line-height: 1.2;
  margin: 0;
}

.history-toggle-button {
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: inline-flex;
  padding: 4px;
}

.visually-hidden {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
  clip: rect(0, 0, 0, 0);
}

.history-list {
  padding: 0 24px 24px;
}

.empty-history {
  color: #979797;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 16px;
  padding: 8px 0;
}

.history-row {
  align-items: center;
  border-bottom: 1px solid #d9d9d9;
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
}

.history-row:last-child {
  border-bottom: 0;
}

.history-row > span {
  display: flex;
  flex-direction: column;
}

.history-target {
  align-items: center;
  flex-direction: row !important;
  gap: 16px;
  text-align: right;
}

.revoke-access-dialog {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 0 24px rgba(51, 51, 51, 0.2);
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
}

.revoke-access-dialog h2 {
  color: #0277bd;
  font-family: 'Raleway', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.revoke-access-dialog-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.revoke-access-dialog-close {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 50%;
  color: #666666;
  cursor: pointer;
  display: inline-flex;
  flex: 0 0 auto;
  height: 36px;
  justify-content: center;
  margin: -8px -8px 0 0;
  padding: 0;
  transition: background-color 0.2s ease;
  width: 36px;
}

.revoke-access-dialog-close:hover:not(:disabled) {
  background: #f5f5f5;
}

.revoke-access-dialog-close:disabled {
  cursor: default;
  opacity: 0.5;
}

.revoke-access-dialog h2 strong {
  font-weight: 700;
}

.revoke-access-dialog-copy {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.revoke-access-dialog-copy p {
  color: #333333;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 18px;
  line-height: 1.5;
  margin: 0;
}

.action-error {
  color: #b71c1c !important;
  font-size: 14px !important;
}

.revoke-access-dialog-actions {
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.revoke-access-dialog-button {
  align-items: center;
  border: 2px solid transparent;
  border-radius: 24px;
  cursor: pointer;
  display: inline-flex;
  font-family: 'Raleway', sans-serif;
  font-size: 18px;
  font-weight: 700;
  height: 48px;
  justify-content: center;
  min-width: 120px;
  padding: 0 24px;
  text-transform: uppercase;
}

.revoke-access-dialog-button-primary {
  background: #db384c;
  border: 2px solid #db384c;
  color: #ffffff;
}

.revoke-access-dialog-button-primary:hover:not(:disabled) {
  background: #b71c1c;
  border-color: #b71c1c;
}

.revoke-access-dialog-button-secondary {
  background: transparent;
  border-color: transparent;
  color: #0277bd;
  min-width: auto;
  padding: 0 12px;
}

.revoke-access-dialog-button-secondary:hover:not(:disabled) {
  background: #e8f3f8;
}

.revoke-access-dialog-button:disabled {
  cursor: default;
  opacity: 0.5;
}

@media (max-width: 800px) {
  .revoke-access-dialog-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .history-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .history-target {
    align-self: flex-end;
  }

  .revoke-access-dialog-button {
    width: 100%;
  }
}
</style>
